import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import LoadingAnimation from "../../../components/animation/LoadingAnimation";
import AdminActionsButton from "../../../components/button/AdminActionsButton";
import NotFoundError from "../../../components/error/NotFoundError";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import H3 from "../../../components/typography/H3";
import { usePopup } from "../../../context/PopupContext";
import { orderService } from "../../../service/OrderService";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import { dateUtils } from "../../../utils/DateUtils";
import OrderDeliveryInfo from "./components/OrderDeliveryInfo";
import OrderDetailsTable from "./components/OrderDetailsTable";
import OrderPriceTable from "./components/OrderPriceTable";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  background-color: ${colors.backgroundTintPrimary};
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);
  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  border-radius: 12px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;
const DetailsWrapper = styled.div`
  padding: 32px;
  background-color: ${colors.backgroundPrimary};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const Top = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
`;
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const InfoSpan = styled.div`
  display: flex;
  gap: 30px;
`;
const Label = styled.span`
  font-size: ${fontSize.s};
  font-weight: 500;
  color: ${colors.black};
`;

const Value = styled.span`
  font-size: ${fontSize.s};
`;
const Bottom = styled.div`
  display: flex;
  gap: 16px;
`;

function OrdersDetailsPage() {
  const { id } = useParams();
  const [order, setOrder] = useState();
  const [loading, setLoading] = useState();
  const [reload, setReload] = useState(false);
  const { showPopup } = usePopup();
  const navigate = useNavigate();

  const statusMap = {
    new: { label: "Accept Order", nextStatus: "ongoing" },
    ongoing: { label: "Ship Order", nextStatus: "shipped" },
    shipped: { label: "Complete Order", nextStatus: "complete" },
  };

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleUpdateStatus = async (status, label) => {
    if (!window.confirm(`Comfirm ${label}`)) {
      return;
    }
    orderService.updateStatus(
      { id: order._id, status: status },
      () => {
        setReload(true);
      },
      {
        default: (message) => {
          alert(message);
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }
    orderService.deleteOrder(
      order._id,
      () => {
        showPopup("", `Order has been deleted successfully..`);
        navigate("/admin/orders");
      },
      {
        default: (message) => {
          alert(message);
        },
      }
    );
  };
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setReload(false);
    const fetchProduct = async () => {
      setLoading(true);
      orderService.getOrderById(
        id,
        (order) => {
          setOrder(order);
          setLoading(false);
        },
        {
          default: (message) => {
            alert(message);
            setOrder(null);
            setLoading(false);
          },
        }
      );
    };
    fetchProduct();
  }, [id, reload]);

  return (
    <Wrapper>
      <Nav>
        <AdminSidebar collapsed={window.innerWidth < 1200} />
      </Nav>
      <Content>
        <Container>
          <H3>Order Details</H3>
          <DetailsWrapper>
            {loading && <LoadingAnimation height={"500px"} />}
            {order == null && !loading && (
              <div
                style={{
                  width: "fit-content",
                  margin: "auto",
                }}
              >
                <NotFoundError message={"This order could not be found."} />
              </div>
            )}
            {order && (
              <>
                <Top>
                  <Details>
                    <InfoSpan>
                      <Label>Order ID:</Label>
                      <Value>{order._id}</Value>
                    </InfoSpan>
                    <InfoSpan>
                      <Label>Order Date:</Label>
                      <Value>{dateUtils.toCombinedDate(order.createdAt)}</Value>
                    </InfoSpan>
                    <InfoSpan>
                      <Label>Customer Name:</Label>
                      <Value>
                        {order.customerDetails.firstName}{" "}
                        {order.customerDetails.lastName}
                      </Value>
                    </InfoSpan>
                  </Details>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <AdminActionsButton
                      variant="secondary"
                      children={"Delete Order"}
                      onClick={() => {
                        handleDelete();
                      }}
                    />
                    {order.status !== "complete" && (
                      <AdminActionsButton
                        children={statusMap[order.status].label}
                        onClick={() => {
                          handleUpdateStatus(
                            statusMap[order.status].nextStatus,
                            statusMap[order.status].label
                          );
                        }}
                      />
                    )}
                  </div>
                </Top>
                <OrderDetailsTable
                  orderId={order._id}
                  orders={order.items}
                  setReload={setReload}
                />
                <Bottom>
                  <OrderPriceTable order={order} />
                  <OrderDeliveryInfo order={order} />
                </Bottom>
              </>
            )}
          </DetailsWrapper>
        </Container>
      </Content>
    </Wrapper>
  );
}

export default OrdersDetailsPage;
